// src/hooks/useProfile.js
import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "./useAuth";
import { wishlistRepository } from "../repositories/wishlistRepository";
import { ratingsRepository } from "../repositories/ratingsRepository";
import { userRepository } from "../repositories/userRepository";
import { productRepository } from "../repositories/productRepository";
import { errorDev, warnDev } from "../utils/logger";

// localStorage queue key (versioned so we can change shape later)
const OFFLINE_QUEUE_KEY = "farmnet_offline_ops_v2";
const OFFLINE_QUEUE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function isPermissionDeniedError(err) {
  if (!err) return false;
  const code = typeof err.code === "string" ? err.code.toLowerCase() : "";
  const msg = typeof err.message === "string" ? err.message.toLowerCase() : "";
  return code.includes("permission-denied") || msg.includes("missing or insufficient permissions");
}

export function useProfile(userId) {
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [ratingStats, setRatingStats] = useState({ avg: null, count: 0 });
  const [loading, setLoading] = useState({
    all: true,
    profile: true,
    products: true,
    wishlist: true,
  });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // Refresh data when auth state changes
    if (currentUser && userId) {
      refresh();
    }
  }, [currentUser, userId]);

  // ----------------------- Profile Fetching -----------------------
  const fetchProfile = useCallback(async () => {
    if (!userId) return;

    setLoading((s) => ({ ...s, profile: true }));
    try {
      // Route through userRepository so this hook shares one profile read
      // definition with the rest of the app instead of building the path inline.
      const profileData = await userRepository.getById(userId);
      if (!mountedRef.current) return;

      setProfile(profileData);
    } catch (err) {
      errorDev("useProfile: failed to fetch profile", err);
    } finally {
      if (mountedRef.current) setLoading((s) => ({ ...s, profile: false }));
    }
  }, [userId]);

  // ----------------------- Real-time Profile Listener -----------------------
  useEffect(() => {
    if (!userId) return;

    // The repository owns the document ref and the listener lifecycle so this
    // hook does not build a Firestore path itself.
    const unsubscribe = userRepository.subscribeById(
      userId,
      (profileData) => {
        if (profileData) {
          setProfile(profileData);
        }
      },
      (err) => {
        errorDev("Profile real-time listener error:", err);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // ----------------------- Products Fetching -----------------------
  const fetchProducts = useCallback(async () => {
    setLoading((s) => ({ ...s, products: true }));
    try {
      // The four ownership fields are queried in parallel and merged by id
      // inside productRepository so this hook shares one ownership query
      // definition with the profile page.
      const rows = await productRepository.listByOwner(userId);
      if (!mountedRef.current) return;
      setProducts(rows);
    } catch (err) {
      errorDev("useProfile: failed to fetch products", err);
    } finally {
      if (mountedRef.current) setLoading((s) => ({ ...s, products: false }));
    }
  }, [userId]);

  // ----------------------- Wishlist System -----------------------
  // Accepts repository rows (plain objects) or raw snapshot documents.
  const normalizeWishlistRows = useCallback(async (rows) => {
    const wishlistMeta = {};
    const itemsLight = [];
    const toFetch = new Set();

    rows.forEach((d) => {
      const data = d.data ? d.data() : d;
      const docId = d.id;
      const pid = data?.productId || data?.product?.id || null;
      
      if (pid) {
        wishlistMeta[pid] = { id: docId, ...data };
        
        if (data.productTitle || data.productImage || data.product) {
          const synthesized = {
            id: pid,
            title: data.productTitle || data.product?.title || "Saved item",
            name: data.productTitle || data.product?.title || "Saved item",
            images: data.productImage ? [data.productImage] : (data.product?.images || []),
            price: data.price ?? null,
            currency: data.currency || "RWF",
            poster: data.sellerId ? { userId: data.sellerId, name: data.sellerName || null } : (data.product?.poster || {}),
            __wishlistDoc: { id: docId, ...data },
          };
          itemsLight.push(synthesized);
        } else {
          toFetch.add(pid);
        }
      } else if (data?.product && data.product.id) {
        itemsLight.push({
          id: data.product.id,
          ...data.product,
          __wishlistDoc: { id: docId, ...data },
        });
      }
    });

    const fetched = [];
    if (toFetch.size > 0) {
      // Batch the enrichment through productRepository so this hook does not
      // build product document refs itself. Missing products fall back to the
      // wishlist metadata instead of dropping the row.
      const products = await productRepository.getByIds(Array.from(toFetch));
      const productById = new Map(products.map((product) => [product.id, product]));
      toFetch.forEach((pid) => {
        const product = productById.get(pid);
        if (product) {
          fetched.push({ ...product, __wishlistDoc: wishlistMeta[pid] });
        } else {
          const meta = wishlistMeta[pid] || {};
          fetched.push({
            id: pid,
            title: meta.productTitle || "Saved item",
            name: meta.productTitle || "Saved item",
            images: meta.productImage ? [meta.productImage] : [],
            price: meta.price ?? null,
            currency: meta.currency || "RWF",
            poster: meta.sellerId ? { userId: meta.sellerId, name: meta.sellerName || null } : {},
            __wishlistDoc: meta.id ? meta : undefined,
          });
        }
      });
    }

    const byId = new Map();
    fetched.forEach((p) => {
      if (p && p.id) byId.set(p.id, p);
    });
    itemsLight.forEach((it) => {
      if (!it || !it.id) return;
      if (!byId.has(it.id)) byId.set(it.id, it);
    });

    return Array.from(byId.values());
  }, []);

  // ----------------------- Real-time Wishlist -----------------------
  // Read through wishlistRepository so this hook shares one wishlist query
  // definition (canonical + legacy merge) with the rest of the app.
  useEffect(() => {
    if (!userId || !currentUser) {
      setWishlist([]);
      setLoading((s) => ({ ...s, wishlist: false }));
      return;
    }

    setLoading((s) => ({ ...s, wishlist: true }));

    let cancelled = false;

    const unsubscribe = wishlistRepository.subscribeAll(
      userId,
      async (rows) => {
        try {
          const items = await normalizeWishlistRows(rows);
          if (cancelled || !mountedRef.current) return;
          setWishlist(items);
          setLoading((s) => ({ ...s, wishlist: false }));
        } catch (err) {
          errorDev("Wishlist snapshot handler error", err);
          if (!cancelled && mountedRef.current) setLoading((s) => ({ ...s, wishlist: false }));
        }
      },
      (err) => {
        errorDev("Wishlist listener error", err);
        if (!cancelled && mountedRef.current) setLoading((s) => ({ ...s, wishlist: false }));
      }
    );

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [userId, currentUser, normalizeWishlistRows]);

  // ----------------------- Enhanced Ratings System -----------------------
  const fetchRatings = useCallback(async () => {
    try {
      const stats = await ratingsRepository.summarizeForTarget(userId);
      if (!mountedRef.current) return;
      setRatingStats(stats);
    } catch (err) {
      errorDev("useProfile: failed to fetch ratings", err);
    }
  }, [userId]);

  // ----------------------- Enhanced Offline Queue -----------------------
  function readQueue() {
    try {
      const raw = JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || "[]");
      if (!Array.isArray(raw)) return [];
      const now = Date.now();
      return raw.filter((item) => {
        if (!item || typeof item !== "object") return false;
        if (typeof item.timestamp !== "number") return false;
        return now - item.timestamp <= OFFLINE_QUEUE_MAX_AGE_MS;
      });
    } catch (err) {
      return [];
    }
  }

  function writeQueue(q) {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(q));
  }

  function pushToQueue(op) {
    const q = readQueue();
    q.push({ ...op, timestamp: Date.now() });
    writeQueue(q);
  }

  const processQueue = useCallback(async () => {
    if (!navigator.onLine || !currentUser) return;
    
    const q = readQueue();
    if (!q.length) return;
    
    const remaining = [];
    
for (const op of q) {
      try {
        switch (op.type) {
          case "updateProfile":
            await userRepository.update(op.payload.uid, op.payload.data, { merge: true });
            break;

          case "deleteProduct":
            await productRepository.delete(op.payload.productId);
            break;

          default:
            warnDev("Unknown operation type:", op.type);
        }
      } catch (err) {
        errorDev("processQueue: op failed", op, err);
        if (!isPermissionDeniedError(err)) {
          remaining.push(op);
        }
      }
    }
    
    writeQueue(remaining);
    if (remaining.length === 0) {
      await refresh();
    }
  }, [currentUser]);

  useEffect(() => {
    window.addEventListener("online", processQueue);
    if (navigator.onLine) processQueue();
    return () => window.removeEventListener("online", processQueue);
  }, [processQueue]);

  // ----------------------- Enhanced Actions -----------------------
  const deleteProduct = async (productId) => {
    if (!isOwner) return;
    
    try {
      await productRepository.delete(productId);
      setProducts(prev => prev.filter(product => product.id !== productId));
    } catch (err) {
      pushToQueue({ type: "deleteProduct", payload: { productId } });
      warnDev("deleteProduct: queued due to error", err);
    }
  };

  const updateProfile = async (updateData) => {
    if (!currentUser) throw new Error("Not signed in");
    
    const uid = currentUser.uid;
    try {
      await userRepository.update(uid, updateData, { merge: true });
      await fetchProfile(); // Refresh local state
    } catch (err) {
      pushToQueue({ type: "updateProfile", payload: { uid, data: updateData } });
      warnDev("updateProfile: queued due to error", err);
    }
  };

  const refresh = async () => {
    if (!userId) return;
    
    setLoading((s) => ({ ...s, all: true }));
     await Promise.all([
      fetchProfile(), 
      fetchProducts(), 
      fetchRatings()
    ]);
    setLoading((s) => ({ ...s, all: false }));
  };

  // ----------------------- Initial Data Load -----------------------
  useEffect(() => {
    async function loadAllData() {
      if (!userId) return;
      
      setLoading((s) => ({ ...s, all: true }));
      await Promise.all([
        fetchProfile(), 
        fetchProducts(), 
        fetchRatings()
      ]);
      
      // Only fetch wishlist if user is signed in
      if (currentUser) {
        // Wishlist is handled by real-time listener
      } else {
        setWishlist([]);
        setLoading((s) => ({ ...s, wishlist: false }));
      }
      
      setLoading((s) => ({ ...s, all: false }));
    }

    loadAllData();
  }, [userId, currentUser]);

  return {
    // Data
    profile,
    products,
    wishlist,
    ratingStats,
    
    // State
    loading,
    currentUser,
    signedIn: !!currentUser,
    isOwner: !!currentUser && currentUser.uid === userId,
    
    // Actions
    deleteProduct,
    updateProfile,
    refresh,
    processQueue,
    
    // Enhanced status
    hasProducts: products.length > 0,
    hasWishlist: wishlist.length > 0,
    isPrivate: profile?.profilePublic === false,
  };
}